import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient, useQueries } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  ArrowLeft,
  X,
  Plus,
  Lightbulb,
  Target,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Clock,
  FileText,
  ArrowRight,
  AlertCircle,
  Search,
  ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, Button, Input, Select, Badge, Checkbox } from '../components/ui';
import { blogsApi, titlesApi } from '../lib/api';
import { useTitleRun } from '../hooks/useTitleRun';
import toast from 'react-hot-toast';
import type { GeneratedTitle, LLMProvider, BlogTone } from '../types';

const llmProviderOptions = [
  { value: 'openai', label: 'OpenAI (GPT-4o)' },
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'gemini', label: 'Google (Gemini)' },
  { value: 'groq', label: 'Groq (Llama)' },
];

const toneOptions = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'technical', label: 'Technical' },
  { value: 'friendly', label: 'Friendly' },
];

const wordCountOptions = [
  { value: '500', label: '500 words (Short)' },
  { value: '1000', label: '1000 words (Medium)' },
  { value: '1500', label: '1500 words (Standard)' },
  { value: '2000', label: '2000 words (Long)' },
  { value: '3000', label: '3000 words (Detailed)' },
  { value: '5000', label: '5000 words (Comprehensive)' },
];

const NUM_TITLES_MIN = 1;
const NUM_TITLES_MAX = 50;
const MAX_KEYWORDS = 10;

/**
 * brainstorm — entering keywords, before any titles exist
 * titles     — a run is generating (polled) or its titles are ready to review
 * writing    — blogs queued, polling each job
 * done       — every job reached a terminal state
 */
type Phase = 'brainstorm' | 'titles' | 'writing' | 'done';

export const GenerateBlog: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The run id lives in the URL so a refresh (or returning later) resumes
  // polling instead of losing a run that is still generating.
  const [searchParams, setSearchParams] = useSearchParams();

  // Step 1 — brainstorm settings, fixed for the whole run
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [targetAudience, setTargetAudience] = useState('general audience');
  const [tone, setTone] = useState<BlogTone>('professional');
  const [wordCount, setWordCount] = useState(1500);
  const [llmProvider, setLlmProvider] = useState<LLMProvider>('gemini');
  const [numTitles, setNumTitles] = useState(5);

  // Step 2 — SEO keywords, deliberately separate from the brainstorm set
  const [seoKeywords, setSeoKeywords] = useState<string[]>([]);
  const [seoKeywordInput, setSeoKeywordInput] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showSeoError, setShowSeoError] = useState(false);

  const runIdParam = searchParams.get('run');
  const runId = runIdParam ? parseInt(runIdParam, 10) : null;

  const [phase, setPhase] = useState<Phase>(runId ? 'titles' : 'brainstorm');
  const [batchJobCount, setBatchJobCount] = useState(0);
  const [pollingJobIds, setPollingJobIds] = useState<number[]>([]);

  const { data: run, isLoading: runLoading } = useTitleRun(
    phase === 'titles' ? runId : null
  );

  const titles = run?.titles ?? [];
  const selectableTitles = titles.filter((t) => t.status !== 'used');

  // Poll each blog job after the batch is queued
  const jobQueries = useQueries({
    queries: pollingJobIds.map((jobId) => ({
      queryKey: ['poll-job', jobId],
      queryFn: () => blogsApi.getJobStatus(jobId),
      refetchInterval: (query: any) => {
        const s = query.state.data?.status ?? '';
        return s === 'completed' || s === 'failed' ? false : 8000;
      },
      enabled: phase === 'writing',
    })),
  });

  // Move to 'done' once every polled job is terminal
  useEffect(() => {
    if (phase !== 'writing' || pollingJobIds.length === 0) return;
    if (jobQueries.length === 0) return;
    const allLoaded = jobQueries.every((q) => !q.isLoading || q.data !== undefined);
    if (!allLoaded) return;
    const allTerminal = jobQueries.every((q) => {
      const s = q.data?.status ?? '';
      return s === 'completed' || s === 'failed';
    });
    if (allTerminal) {
      setPhase('done');
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    }
  }, [jobQueries, phase, pollingJobIds.length]);

  // ── Keyword chip helpers ───────────────────────────────────────────────────
  const addKeyword = () => {
    const trimmed = keywordInput.trim();
    if (trimmed && !keywords.includes(trimmed) && keywords.length < MAX_KEYWORDS) {
      setKeywords([...keywords, trimmed]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeyword();
    }
  };

  /** Returns the SEO keyword list including any text not yet committed. */
  const commitSeoKeyword = (): string[] => {
    const trimmed = seoKeywordInput.trim();
    if (trimmed && !seoKeywords.includes(trimmed) && seoKeywords.length < MAX_KEYWORDS) {
      const next = [...seoKeywords, trimmed];
      setSeoKeywords(next);
      setSeoKeywordInput('');
      return next;
    }
    return seoKeywords;
  };

  const removeSeoKeyword = (index: number) => {
    setSeoKeywords(seoKeywords.filter((_, i) => i !== index));
  };

  const handleSeoKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitSeoKeyword();
      setShowSeoError(false);
    }
  };

  // ── Selection helpers ──────────────────────────────────────────────────────
  const toggleTitle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allSelected =
    selectableTitles.length > 0 && selectableTitles.every((t) => selectedIds.has(t.id));
  const someSelected = selectableTitles.some((t) => selectedIds.has(t.id));

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        selectableTitles.forEach((t) => next.delete(t.id));
      } else {
        selectableTitles.forEach((t) => next.add(t.id));
      }
      return next;
    });
  };

  // ── Mutations ──────────────────────────────────────────────────────────────
  const createRunMutation = useMutation({
    mutationFn: () =>
      titlesApi.createRun({
        keywords,
        target_audience: targetAudience,
        tone,
        word_count: wordCount,
        llm_provider: llmProvider,
        num_titles: numTitles,
      }),
    onSuccess: (data) => {
      setSearchParams({ run: String(data.run_id) }, { replace: true });
      setPhase('titles');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to start title generation');
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: () => titlesApi.regenerate(runId as number, { num_titles: numTitles }),
    onSuccess: (data) => {
      // Seed the cache with the now-processing run so polling resumes without
      // an extra round trip. Selections are deliberately left intact.
      queryClient.setQueryData(['title-run', runId], data);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to generate more titles');
    },
  });

  const batchWriteMutation = useMutation({
    mutationFn: (payload: { title_ids: number[]; seo_keywords: string[] }) =>
      blogsApi.batchWrite(payload),
    onSuccess: (data) => {
      setBatchJobCount(data.jobs.length);
      setPollingJobIds(data.jobs.map((j) => j.job_id));
      setSelectedIds(new Set());
      setPhase('writing');
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      queryClient.invalidateQueries({ queryKey: ['title-run', runId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to queue blogs');
    },
  });

  const handleGenerateTitles = () => createRunMutation.mutate();

  const handleWriteBlogs = () => {
    // Commit a keyword the user typed but did not press Enter on, rather than
    // silently discarding it and blocking on "SEO keywords are required".
    const finalSeo = commitSeoKeyword();
    if (finalSeo.length === 0) {
      setShowSeoError(true);
      return;
    }
    batchWriteMutation.mutate({
      title_ids: Array.from(selectedIds),
      seo_keywords: finalSeo,
    });
  };

  const handleReset = () => {
    setSearchParams({}, { replace: true });
    setPhase('brainstorm');
    setBatchJobCount(0);
    setPollingJobIds([]);
    setSelectedIds(new Set());
    setSeoKeywords([]);
    setSeoKeywordInput('');
    setShowSeoError(false);
    setKeywords([]);
    setKeywordInput('');
    setTargetAudience('general audience');
    setTone('professional');
    setWordCount(1500);
    setLlmProvider('gemini');
    setNumTitles(5);
  };

  /** Back to the same run's title list — the remaining titles are still there. */
  const handleBackToTitles = () => {
    setPhase('titles');
    setBatchJobCount(0);
    setPollingJobIds([]);
    setSeoKeywords([]);
    setSeoKeywordInput('');
    setShowSeoError(false);
    queryClient.invalidateQueries({ queryKey: ['title-run', runId] });
  };

  // ── Step 1: brainstorm form ────────────────────────────────────────────────
  const renderInputForm = () => (
    <motion.div
      key="brainstorm"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Generate Blog Titles
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          Add keywords to brainstorm titles from. You'll pick which ones to write next.
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex gap-2">
          <Input
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a keyword and press Enter"
            leftIcon={<Lightbulb className="h-5 w-5" />}
          />
          <Button onClick={addKeyword} disabled={!keywordInput.trim()}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {keywords.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {keywords.map((keyword, index) => (
              <motion.span
                key={keyword}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-sm font-medium"
              >
                {keyword}
                <button
                  onClick={() => removeKeyword(index)}
                  className="hover:text-primary-900 dark:hover:text-primary-200"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </motion.span>
            ))}
          </div>
        )}

        <p className="text-sm text-gray-500 dark:text-gray-400">
          {keywords.length}/{MAX_KEYWORDS} keywords added
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Target Audience"
          value={targetAudience}
          onChange={(e) => setTargetAudience(e.target.value)}
          placeholder="e.g., small business owners, developers"
          leftIcon={<Target className="h-5 w-5" />}
        />
        <Select
          label="Tone"
          value={tone}
          onChange={(e) => setTone(e.target.value as BlogTone)}
          options={toneOptions}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select
          label="Word Count"
          value={wordCount.toString()}
          onChange={(e) => setWordCount(parseInt(e.target.value))}
          options={wordCountOptions}
          helperText="Applies to every blog in this run"
        />
        <Input
          label="Number of Titles"
          type="number"
          min={NUM_TITLES_MIN}
          max={NUM_TITLES_MAX}
          value={numTitles.toString()}
          onChange={(e) => {
            const val = parseInt(e.target.value);
            if (!isNaN(val) && val >= NUM_TITLES_MIN && val <= NUM_TITLES_MAX) {
              setNumTitles(val);
            }
          }}
          helperText={`1–${NUM_TITLES_MAX} titles per request`}
        />
        <Select
          label="AI Provider"
          value={llmProvider}
          onChange={(e) => setLlmProvider(e.target.value as LLMProvider)}
          options={llmProviderOptions}
          helperText="Different providers may produce different styles"
        />
      </div>

      <Button
        onClick={handleGenerateTitles}
        size="xl"
        className="w-full"
        disabled={keywords.length < 1 || createRunMutation.isPending}
        isLoading={createRunMutation.isPending}
        leftIcon={<Sparkles className="h-5 w-5" />}
      >
        Generate Titles
      </Button>
    </motion.div>
  );

  // ── Step 2: title review and selection ─────────────────────────────────────
  const isGenerating =
    !run || run.status === 'pending' || run.status === 'processing' || runLoading;

  const renderTitlesView = () => {
    if (isGenerating) {
      return (
        <motion.div
          key="generating"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6 text-center py-6"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500">
            <Loader2 className="h-10 w-10 text-white animate-spin" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Generating Titles...
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              {titles.length > 0
                ? `Adding more titles to the ${titles.length} already generated...`
                : 'AI is creating blog titles from your keywords.'}
            </p>
          </div>
          {run && run.keywords.length > 0 && (
            <div className="flex flex-wrap gap-1 justify-center">
              {run.keywords.map((kw) => (
                <Badge key={kw} variant="primary" size="sm">{kw}</Badge>
              ))}
            </div>
          )}
        </motion.div>
      );
    }

    if (run.status === 'failed') {
      const isAuthExpired = run.error_code === 'claude_auth_expired';
      return (
        <motion.div
          key="failed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-red-700 dark:text-red-400">
                {isAuthExpired ? 'Claude token expired' : 'Title generation failed'}
              </p>
              <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                {isAuthExpired
                  ? 'Blog generation is paused until a new credential is provided.'
                  : run.error || 'Something went wrong.'}
              </p>
              {isAuthExpired && (
                <Link
                  to="/settings"
                  className="inline-block mt-2 text-sm font-medium text-red-700 dark:text-red-300 hover:underline"
                >
                  Go to Settings →
                </Link>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => regenerateMutation.mutate()}
              className="flex-1"
              isLoading={regenerateMutation.isPending}
              leftIcon={<RefreshCw className="h-4 w-4" />}
            >
              Try Again
            </Button>
            <Button variant="outline" onClick={handleReset} className="flex-1">
              Start Over
            </Button>
          </div>
        </motion.div>
      );
    }

    let lastBatch = 0;

    return (
      <motion.div
        key="titles"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Select Titles to Write
          </h2>
          <p className="text-gray-500 dark:text-gray-400">
            Pick the titles you want turned into blogs. {titles.length} generated
            {run.keywords.length > 0 && <> from <span className="font-medium">{run.keywords.join(', ')}</span></>}.
          </p>
        </div>

        {/* Select all */}
        {selectableTitles.length > 0 && (
          <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
            <Checkbox
              checked={allSelected}
              indeterminate={!allSelected && someSelected}
              onChange={toggleAll}
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {selectedIds.size > 0
                ? `${selectedIds.size} selected`
                : `Select all ${selectableTitles.length}`}
            </span>
          </div>
        )}

        {/* Title list */}
        <div className="space-y-2">
          {titles.map((t: GeneratedTitle, i: number) => {
            const isUsed = t.status === 'used';
            const isSelected = selectedIds.has(t.id);
            const showDivider = t.generation_batch !== lastBatch && lastBatch !== 0;
            lastBatch = t.generation_batch;

            return (
              <React.Fragment key={t.id}>
                {showDivider && (
                  <div className="flex items-center gap-2 pt-3 pb-1">
                    <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      Batch {t.generation_batch}
                    </span>
                    <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                  </div>
                )}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.03, 0.3) }}
                >
                  <Card
                    className={`transition-colors ${
                      isUsed
                        ? 'opacity-60'
                        : isSelected
                        ? 'border-primary-400 dark:border-primary-600 bg-primary-50/50 dark:bg-primary-900/10 cursor-pointer'
                        : 'cursor-pointer hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                    onClick={() => !isUsed && toggleTitle(t.id)}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start gap-3">
                        <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            disabled={isUsed}
                            onChange={() => toggleTitle(t.id)}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {t.title}
                            </p>
                            {isUsed && (
                              <Badge variant="secondary" size="sm" className="flex-shrink-0">
                                Written
                              </Badge>
                            )}
                          </div>
                          {t.description && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {t.description}
                            </p>
                          )}
                          {isUsed && t.blog_job_id && (
                            <Link
                              to={`/blogs/${t.blog_job_id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline mt-1"
                            >
                              View blog <ExternalLink className="h-3 w-3" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Regenerate */}
        <Button
          variant="outline"
          onClick={() => regenerateMutation.mutate()}
          isLoading={regenerateMutation.isPending}
          className="w-full"
          leftIcon={<RefreshCw className="h-4 w-4" />}
        >
          Generate More Titles
        </Button>

        {/* SEO keywords — only once something is selected */}
        <AnimatePresence>
          {selectedIds.size > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3 overflow-hidden"
            >
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mt-4">
                  SEO Keywords <span className="text-red-500">*</span>
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Keywords the article content should rank for. These are separate from the
                  keywords used to brainstorm the titles.
                </p>
              </div>

              <div className="flex gap-2">
                <Input
                  value={seoKeywordInput}
                  onChange={(e) => setSeoKeywordInput(e.target.value)}
                  onKeyDown={handleSeoKeyDown}
                  placeholder="Type an SEO keyword and press Enter"
                  leftIcon={<Search className="h-5 w-5" />}
                  error={showSeoError && seoKeywords.length === 0 ? 'At least one SEO keyword is required' : undefined}
                />
                <Button
                  onClick={() => {
                    commitSeoKeyword();
                    setShowSeoError(false);
                  }}
                  disabled={!seoKeywordInput.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {seoKeywords.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {seoKeywords.map((kw, index) => (
                    <motion.span
                      key={kw}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary-100 dark:bg-secondary-900/30 text-secondary-700 dark:text-secondary-400 text-sm font-medium"
                    >
                      {kw}
                      <button
                        onClick={() => removeSeoKeyword(index)}
                        className="hover:text-secondary-900 dark:hover:text-secondary-200"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </motion.span>
                  ))}
                </div>
              )}

              <Button
                onClick={handleWriteBlogs}
                size="xl"
                className="w-full"
                isLoading={batchWriteMutation.isPending}
                leftIcon={<Sparkles className="h-5 w-5" />}
              >
                Generate {selectedIds.size} Blog{selectedIds.size !== 1 ? 's' : ''}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  // ── Step 3: writing ────────────────────────────────────────────────────────
  const getJobStatusIcon = (status: string) => {
    if (status === 'completed') return <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />;
    if (status === 'failed')    return <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />;
    if (status.startsWith('retrying')) return <RefreshCw className="h-4 w-4 text-orange-400 animate-spin flex-shrink-0" />;
    if (status === 'processing') return <Loader2 className="h-4 w-4 text-blue-500 animate-spin flex-shrink-0" />;
    return <Clock className="h-4 w-4 text-yellow-500 flex-shrink-0" />;
  };

  const pollingCompleted = jobQueries.filter((q) => q.data?.status === 'completed').length;
  const pollingFailed    = jobQueries.filter((q) => q.data?.status === 'failed').length;
  const pollingDone      = pollingCompleted + pollingFailed;

  const renderProcessingView = () => (
    <motion.div
      key="writing"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 mb-6">
          {phase === 'done' ? (
            <CheckCircle className="h-10 w-10 text-white" />
          ) : (
            <Loader2 className="h-10 w-10 text-white animate-spin" />
          )}
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {phase === 'writing' ? 'Writing Blogs...' : 'All Blogs Written!'}
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          {phase === 'writing'
            ? `${pollingDone} of ${batchJobCount} blogs completed...`
            : `${pollingCompleted} blog${pollingCompleted !== 1 ? 's' : ''} written${pollingFailed > 0 ? `, ${pollingFailed} failed` : ''}.`}
        </p>
      </div>

      <div className="w-full">
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-primary-500 to-secondary-500"
            initial={{ width: '0%' }}
            animate={{
              width:
                phase === 'done'
                  ? '100%'
                  : `${batchJobCount > 0 ? (pollingDone / batchJobCount) * 100 : 0}%`,
            }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {seoKeywords.length > 0 && (
        <Card className="bg-gray-50 dark:bg-gray-800/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                SEO Keywords
              </span>
              <div className="flex flex-wrap gap-1 justify-end max-w-[70%]">
                {seoKeywords.map((kw) => (
                  <Badge key={kw} variant="secondary" size="sm">{kw}</Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {jobQueries.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
            {batchJobCount} blog{batchJobCount !== 1 ? 's' : ''} in progress
          </h3>
          {jobQueries.map((q, i) => {
            const status = q.data?.status ?? 'pending';
            const title  = q.data?.selected_title ?? `Blog ${i + 1}`;
            return (
              <motion.div
                key={pollingJobIds[i]}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
              >
                <Card className={
                  status === 'completed' ? 'border-green-200 dark:border-green-800' :
                  status === 'failed'    ? 'border-red-200 dark:border-red-800' : ''
                }>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      {getJobStatusIcon(status)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 capitalize">{status}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {phase === 'done' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row gap-3"
        >
          <Button
            onClick={() => navigate('/blogs')}
            className="flex-1"
            leftIcon={<FileText className="h-4 w-4" />}
            rightIcon={<ArrowRight className="h-4 w-4" />}
          >
            View My Blogs
          </Button>
          {/* The run's remaining titles are still stored, so going back is free */}
          <Button
            variant="outline"
            onClick={handleBackToTitles}
            className="flex-1"
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back to Titles
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
            className="flex-1"
            leftIcon={<Sparkles className="h-4 w-4" />}
          >
            Start Over
          </Button>
        </motion.div>
      )}
    </motion.div>
  );

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      {(phase === 'brainstorm' || phase === 'titles') && (
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            onClick={() => (phase === 'titles' ? handleReset() : navigate(-1))}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Auto Blog Generator
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {phase === 'brainstorm'
                ? 'Enter keywords to generate title ideas'
                : 'Review the titles and choose which to write'}
            </p>
          </div>
        </div>
      )}

      <Card className="mb-6">
        <CardContent className="p-6 md:p-8">
          <AnimatePresence mode="wait">
            {phase === 'brainstorm' && renderInputForm()}
            {phase === 'titles' && renderTitlesView()}
            {(phase === 'writing' || phase === 'done') && renderProcessingView()}
          </AnimatePresence>
        </CardContent>
      </Card>
    </div>
  );
};
